import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import { apiGet, buildQueryString } from "lib/apiClient";
import { formatKes } from "lib/format";
import { sumAmounts } from "lib/partnerPages";

export const PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
];

export function periodRange(key) {
  const now = new Date();
  if (key === "all") return { from: undefined, to: undefined };
  if (key === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: undefined };
  }
  const days = Number.parseInt(key, 10);
  return { from: new Date(now.getTime() - days * 86400000).toISOString(), to: undefined };
}

const n = (value) => Number(value ?? 0);
const pctOf = (part, whole) => (n(whole) > 0 ? (n(part) / n(whole)) * 100 : 0);
export const bpsLabel = (bps) => `${Number(bps ?? 0) / 100}%`;

export function useRevenueStatement(basePath, { tenantId, period, pollMs = 15000 }) {
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      try {
        const { from, to } = periodRange(period);
        const result = await apiGet(`${basePath}/statement${buildQueryString({ tenantId, from, to })}`);
        setStatement(result.statement);
        setError(null);
      } catch (err) {
        if (!background) setError(err.message || "Failed to load the revenue statement.");
      } finally {
        if (!background) setLoading(false);
      }
    },
    [basePath, tenantId, period],
  );

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  return { statement, error, loading, reload: load };
}

export function usePartnerRevenueBreakdown(period, { pollMs = 30000 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (background = false) => {
      try {
        const { from, to } = periodRange(period);
        const result = await apiGet(`/portal/revenue/partners${buildQueryString({ from, to })}`);
        setData(result);
        setError(null);
      } catch (err) {
        if (!background) setError(err.message || "Failed to load revenue by partner.");
      }
    },
    [period],
  );

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  return { data, error, reload: load };
}

export function useOwnPartnerRevenue(period, { pollMs = 15000 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (background = false) => {
      try {
        const { from, to } = periodRange(period);
        const result = await apiGet(`/portal/partner/revenue${buildQueryString({ from, to })}`);
        setData(result);
        setError(null);
      } catch (err) {
        if (!background) setError(err.message || "Failed to load your revenue.");
      }
    },
    [period],
  );

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  return { data, error };
}

let marketTitleCache = null;
let marketTitleRequest = null;

function loadMarketTitles() {
  if (marketTitleCache) return Promise.resolve(marketTitleCache);
  if (!marketTitleRequest) {
    marketTitleRequest = apiGet(`/portal/markets${buildQueryString({ page: 1, pageSize: 100 })}`)
      .then((result) => {
        marketTitleCache = new Map((result.markets || []).map((m) => [m.id, m.question]));
        return marketTitleCache;
      })
      .catch(() => {
        marketTitleRequest = null;
        return new Map();
      });
  }
  return marketTitleRequest;
}

export function useMarketTitles() {
  const [titles, setTitles] = useState(() => marketTitleCache || new Map());
  useEffect(() => {
    let cancelled = false;
    loadMarketTitles().then((map) => {
      if (!cancelled) setTitles(map);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return titles;
}

export function PeriodPicker({ value, onChange, periods = PERIODS }) {
  return (
    <div className="inline-flex min-h-9 flex-wrap items-center gap-0.5 rounded-lg bg-muted p-1 text-muted-foreground" role="group" aria-label="Period">
      {periods.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(p.key)}
          aria-pressed={value === p.key}
          className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
            value === p.key ? "bg-background text-foreground shadow-sm dark:bg-input/40" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function Tile({ label, value, hint, tone = "default", emphasis = false }) {
  const tones = {
    default: "text-navy-700 dark:text-white",
    green: "text-green-600 dark:text-green-400",
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-brand-500 dark:text-brand-400",
    violet: "text-purple-600 dark:text-purple-400",
  };
  return (
    <div className={`min-w-0 rounded-xl border p-4 ${emphasis ? "border-brand-500/40 bg-brand-50/40 dark:bg-brand-500/10" : "border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800"}`}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`num mt-1 truncate text-lg font-semibold sm:text-xl ${tones[tone]}`} title={value}>
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function TileSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-[92px] animate-pulse rounded-md bg-accent" />
      ))}
    </div>
  );
}

function youKeep(s) {
  return n(s.tenant.total) - n(s.partners.earned);
}

export function RevenueHeadline({ statement }) {
  if (!statement) return <TileSkeleton />;
  const s = statement;
  const cur = s.currency;
  const tenantPct = bpsLabel(s.tenant.shareBps);
  const platformPct = bpsLabel(s.platform.shareBps);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
      <Tile label="Revenue earned" value={formatKes(s.gross.total, cur)} hint="Fees and commission, after payment processing" />
      <Tile label="Shared revenue" value={formatKes(s.net, cur)} tone="blue" hint={`Revenue less your SMS fees, split ${tenantPct} you, ${platformPct} platform`} />
      <Tile
        label={`Your share (${tenantPct} + SMS)`}
        value={formatKes(s.tenant.total, cur)}
        tone="green"
        hint={`${formatKes(s.tenant.netShare, cur)} share + ${formatKes(s.tenant.sms, cur)} SMS`}
      />
      <Tile label={`Infrastructure provider (${platformPct})`} value={formatKes(s.platform.total, cur)} tone="violet" hint="The infrastructure provider's fee, its share of net revenue" />
      <Tile label="Paid to partners" value={formatKes(s.partners.earned, cur)} tone="amber" hint="Their share of what their markets earned" />
      <Tile label="You keep" value={formatKes(youKeep(s), cur)} tone="green" emphasis hint="Your share less what partners earned" />
    </div>
  );
}

export function AllocationBar({ statement }) {
  if (!statement) return null;
  const s = statement;
  const whole = n(s.gross.total);
  const parts = [
    { key: "platform", label: "Infrastructure provider", amount: s.platform.total, color: "bg-purple-500" },
    { key: "partners", label: "Partners", amount: s.partners.earned, color: "bg-amber-400" },
    { key: "keep", label: "You keep (net share)", amount: n(s.tenant.netShare) - n(s.partners.earned), color: "bg-primary" },
    { key: "sms", label: "Your SMS fees", amount: s.tenant.sms, color: "bg-brand-300" },
  ];
  const allocated = parts.reduce((sum, p) => sum + Math.max(0, n(p.amount)), 0);
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900" role="img" aria-label="Allocation of revenue earned">
        {whole > 0 && parts.map((p) => <div key={p.key} className={p.color} style={{ width: `${pctOf(Math.max(0, n(p.amount)), Math.max(whole, allocated))}%` }} />)}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {parts.map((p) => (
          <div key={p.key} className="flex min-w-0 items-center gap-2">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.color}`} />
            <div className="min-w-0">
              <p className="truncate text-xs text-gray-500">{p.label}</p>
              <p className="num text-sm font-semibold text-navy-700 dark:text-white">
                {formatKes(p.amount, s.currency)} <span className="text-xs font-medium text-gray-500">{pctOf(p.amount, whole).toFixed(1)}%</span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, currency, sub, strong, negative, indent }) {
  return (
    <tr className="border-t border-gray-100 first:border-0 dark:border-white/5">
      <td className={`py-2 pr-3 text-sm ${indent ? "pl-4 text-gray-500" : strong ? "font-semibold text-navy-700 dark:text-white" : "text-gray-600 dark:text-gray-300"}`}>
        {label}
        {sub && <span className="block text-xs text-gray-400">{sub}</span>}
      </td>
      <td className={`num whitespace-nowrap py-2 text-right text-sm ${strong ? "font-semibold text-navy-700 dark:text-white" : "text-navy-700 dark:text-gray-200"}`}>
        {negative ? "− " : ""}
        {formatKes(value, currency)}
      </td>
    </tr>
  );
}

export function RevenueWaterfall({ statement }) {
  if (!statement) return null;
  const s = statement;
  const c = s.currency;
  const tenantPct = bpsLabel(s.tenant.shareBps);
  const platformPct = bpsLabel(s.platform.shareBps);
  return (
    <table className="w-full">
      <tbody>
        <Row label="Deposit fees" sub="After payment processing" value={s.gross.depositFees} currency={c} indent />
        <Row label="Withdrawal fees" sub="After payment processing" value={s.gross.withdrawalFees} currency={c} indent />
        <Row label="SMS fees (KES 1 per deposit)" value={s.gross.smsFees} currency={c} indent />
        <Row label="Trading commission (10% of profits)" value={s.gross.tradingCommission} currency={c} indent />
        <Row label="Settlement surplus" sub="Pool left over after payouts, less the market's own seed" value={s.gross.settlementSurplus} currency={c} indent />
        <Row label="Revenue earned" value={s.gross.total} currency={c} strong />
        <Row label="SMS fees, yours in full" value={s.gross.smsFees} currency={c} negative indent />
        <Row label="Shared revenue" sub="Split between you and the platform" value={s.net} currency={c} strong />
        {n(s.absorbedByPlatform) > 0 ? (
          <Row label="Covered by the platform" sub="Taken from the platform's share, not yours" value={s.absorbedByPlatform} currency={c} negative indent />
        ) : null}
        <Row label={`Infrastructure provider, ${platformPct} of net`} value={s.platform.total} currency={c} indent />
        <Row label={`Your share, ${tenantPct} of net`} value={s.tenant.netShare} currency={c} indent />
        <Row label="Your SMS fees" value={s.tenant.sms} currency={c} indent />
        <Row label="Your share total" value={s.tenant.total} currency={c} strong />
        <Row label="Paid to partners" sub="Their share of what their markets earned" value={s.partners.earned} currency={c} negative indent />
        <Row label="You keep" value={youKeep(s)} currency={c} strong />
      </tbody>
    </table>
  );
}

export function VolumeStrip({ statement }) {
  if (!statement) return null;
  const v = statement.volumes;
  const c = statement.currency;
  const netFlow = n(v.depositPrincipal) - n(v.withdrawalPrincipal);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Tile label={`Money in · ${v.depositCount} deposit${v.depositCount === 1 ? "" : "s"}`} value={formatKes(v.depositPrincipal, c)} tone="green" />
      <Tile label={`Money out · ${v.withdrawalCount} withdrawal${v.withdrawalCount === 1 ? "" : "s"}`} value={formatKes(v.withdrawalPrincipal, c)} tone="amber" />
      <Tile label="Net inflow" value={formatKes(netFlow, c)} tone={netFlow >= 0 ? "blue" : "amber"} hint="Deposits less withdrawals" />
    </div>
  );
}

const FLOW_META = {
  DEPOSIT: { label: "Deposit", badge: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300" },
  WITHDRAWAL: { label: "Withdrawal", badge: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
  COMMISSION: { label: "Commission", badge: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" },
  SETTLEMENT_SURPLUS: { label: "Settlement surplus", badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300" },
  PARTNER_PAYOUT: { label: "Partner payout", badge: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300" },
};
const DIRECTION = { IN: "In", OUT: "Out", REVENUE: "Revenue" };

function marketLabel(row, titles) {
  if (!row.marketId) return "Wallet movement";
  return titles.get(row.marketId) || `Market ${row.marketId.slice(-6)}`;
}

function csvCell(value) {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function toCsv(head, lines) {
  return [head, ...lines].map((line) => line.map(csvCell).join(",")).join("\n");
}

function tenantCsv(rows, titles) {
  return toCsv(
    ["date", "type", "direction", "market", "partner", "amount", "platformFeesCharged", "sms", "net", "platform", "tenant", "partnerShare", "tenantRetained"],
    rows.map((r) => [
      r.at,
      FLOW_META[r.kind]?.label ?? r.kind,
      r.direction,
      r.marketId ? marketLabel(r, titles) : "",
      r.partnerName || "",
      r.amount,
      r.platformFee,
      r.split.sms,
      r.split.net,
      r.split.platform,
      r.split.tenant,
      r.split.partner,
      r.split.tenantRetained,
    ]),
  );
}

function partnerCsv(rows, titles) {
  return toCsv(
    ["date", "market", "net", "yourShare"],
    rows.map((r) => [r.at, marketLabel(r, titles), r.split.net, r.split.partner]),
  );
}

function formatWhen(at) {
  return new Date(at).toLocaleString("en-KE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const numCell = "num whitespace-nowrap py-2 pr-3 text-right text-sm";

function TenantFlowRow({ r, titles }) {
  const meta = FLOW_META[r.kind] ?? { label: r.kind, badge: "bg-gray-100 text-gray-700" };
  const c = r.currency;
  const sign = r.direction === "OUT" ? "−" : r.direction === "IN" ? "+" : "";
  return (
    <tr className="border-t border-gray-100 align-top dark:border-white/5">
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-gray-600 dark:text-gray-300">{formatWhen(r.at)}</td>
      <td className="py-2 pr-3">
        <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}>{meta.label}</span>
        <span className="ml-1.5 text-[11px] text-gray-400">{DIRECTION[r.direction]}</span>
        {n(r.split.sms) > 0 && <span className="block text-[11px] text-gray-400">incl. {formatKes(r.split.sms, c)} SMS</span>}
        {r.kind === "SETTLEMENT_SURPLUS" && n(r.seed) > 0 && <span className="block text-[11px] text-gray-400">less {formatKes(r.seed, c)} seed returned</span>}
      </td>
      <td className="max-w-[220px] py-2 pr-3 text-xs">
        {r.marketId ? (
          <Link to={`/portal/markets/${r.marketId}`} className="line-clamp-2 font-medium text-navy-700 hover:text-brand-500 dark:text-white">
            {marketLabel(r, titles)}
          </Link>
        ) : (
          <span className="text-gray-400">{marketLabel(r, titles)}</span>
        )}
        {r.partnerName && <span className="block text-[11px] text-purple-600 dark:text-purple-300">{r.partnerName}</span>}
      </td>
      <td className={`${numCell} font-semibold ${r.direction === "IN" ? "text-green-600 dark:text-green-400" : r.direction === "OUT" ? "text-amber-600 dark:text-amber-400" : "text-navy-700 dark:text-white"}`}>
        {sign}
        {formatKes(r.amount, c)}
      </td>
      <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{formatKes(r.platformFee, c)}</td>
      <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{formatKes(r.split.net, c)}</td>
      <td className={`${numCell} hidden text-gray-600 dark:text-gray-300 md:table-cell`}>{formatKes(r.split.platform, c)}</td>
      <td className={`${numCell} font-semibold text-navy-700 dark:text-white`}>{formatKes(r.split.tenant, c)}</td>
      <td className="num whitespace-nowrap py-2 text-right text-sm text-gray-600 dark:text-gray-300">{formatKes(r.split.partner, c)}</td>
    </tr>
  );
}

function PartnerFlowRow({ r, titles }) {
  const c = r.currency;
  return (
    <tr className="border-t border-gray-100 align-top dark:border-white/5">
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-gray-600 dark:text-gray-300">{formatWhen(r.at)}</td>
      <td className="py-2 pr-3 text-sm">
        {r.marketId ? (
          <Link to={`/portal/markets/${r.marketId}`} className="line-clamp-2 font-medium text-navy-700 hover:text-brand-500 dark:text-white">
            {marketLabel(r, titles)}
          </Link>
        ) : (
          <span className="text-gray-400">{marketLabel(r, titles)}</span>
        )}
      </td>
      <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{formatKes(r.split.net, c)}</td>
      <td className="num whitespace-nowrap py-2 text-right text-sm font-semibold text-green-600 dark:text-green-400">{formatKes(r.split.partner, c)}</td>
    </tr>
  );
}

const TENANT_HEADERS = [
  { label: "Date" },
  { label: "Type" },
  { label: "Market/partner" },
  { label: "Amount", right: true },
  { label: "Platform fees charged", right: true },
  { label: "Net", right: true },
  { label: "Platform", right: true, hidden: true },
  { label: "Tenant", right: true },
  { label: "Partner", right: true, last: true },
];

const PARTNER_HEADERS = [{ label: "Date" }, { label: "Market" }, { label: "Net", right: true }, { label: "Your share", right: true, last: true }];

export function MoneyFlowLog({
  basePath = "/portal/revenue",
  tenantId,
  period,
  variant = "tenant",
  pageSize = 25,
  pollMs = 15000,
  title = "Money in and out",
  description,
}) {
  const titles = useMarketTitles();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ flows: [], total: 0 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPartnerView = variant === "partner";

  useEffect(() => setPage(1), [tenantId, period]);

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      try {
        const { from, to } = periodRange(period);
        const result = await apiGet(`${basePath}/flows${buildQueryString({ tenantId, from, to, page, pageSize })}`);
        setData(result);
        setError(null);
      } catch (err) {
        if (!background) setError(err.message || "Failed to load money movements.");
      } finally {
        if (!background) setLoading(false);
      }
    },
    [basePath, tenantId, period, page, pageSize],
  );

  useEffect(() => {
    load();
    if (!pollMs || page !== 1) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
  }, [load, pollMs, page]);

  const pages = Math.max(1, Math.ceil(data.total / pageSize));
  const exportHref = useMemo(
    () => `data:text/csv;charset=utf-8,${encodeURIComponent(isPartnerView ? partnerCsv(data.flows, titles) : tenantCsv(data.flows, titles))}`,
    [data.flows, titles, isPartnerView],
  );
  const headers = isPartnerView ? PARTNER_HEADERS : TENANT_HEADERS;
  const colSpan = headers.length;
  const intro =
    description ??
    (isPartnerView
      ? "Revenue from your markets, newest first. Your share is worked out on each row, so the rows add up to your total."
      : "Deposits and withdrawals, commission and settlement surplus. Each row shows how its own revenue splits, so the rows add up to the statement.");

  return (
    <Card extra="p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-navy-700 dark:text-white">{title}</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{intro}</p>
        </div>
        <a
          href={exportHref}
          download={isPartnerView ? "my-revenue.csv" : "money-flows.csv"}
          className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
        >
          Export page (CSV)
        </a>
      </div>
      {error && <div className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
      <div className="mt-4 overflow-x-auto">
        <table className={`w-full text-left ${isPartnerView ? "min-w-[480px]" : "min-w-[860px]"}`}>
          <thead>
            <tr className="text-xs text-gray-500">
              {headers.map((h) => (
                <th key={h.label} className={`pb-2 font-semibold ${h.last ? "" : "pr-3"} ${h.right ? "text-right" : ""} ${h.hidden ? "hidden md:table-cell" : ""}`}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              data.flows.length === 0 &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={colSpan} className="py-2">
                    <div className="h-6 animate-pulse rounded-md bg-accent" />
                  </td>
                </tr>
              ))}
            {!loading && data.flows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="py-6 text-center text-sm text-gray-500">
                  {isPartnerView ? "Your markets have not earned revenue in this period." : "No money moved in this period."}
                </td>
              </tr>
            )}
            {data.flows.map((r) =>
              isPartnerView ? <PartnerFlowRow key={`${r.kind}:${r.id}`} r={r} titles={titles} /> : <TenantFlowRow key={`${r.kind}:${r.id}`} r={r} titles={titles} />,
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500">
        <span>
          {data.total.toLocaleString()} {isPartnerView ? "row" : "movement"}
          {data.total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-sm border border-gray-200 px-3 py-1 font-semibold disabled:opacity-40 dark:border-white/10">
            Previous
          </button>
          <span>
            {page} / {pages}
          </span>
          <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-sm border border-gray-200 px-3 py-1 font-semibold disabled:opacity-40 dark:border-white/10">
            Next
          </button>
        </div>
      </div>
    </Card>
  );
}

const STATUS_BADGE = {
  ACTIVE: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  CLOSED: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300",
};

const STATUS_LABEL = { ACTIVE: "Active", SUSPENDED: "Paused", CLOSED: "Closed" };

// A row from an API that predates the main-market share has no ownSplit or main: the
// whole split is then own markets.
const ownSplitOf = (p) => p.ownSplit ?? p.split;

export function PartnerRevenueTable({ period }) {
  const { data, error } = usePartnerRevenueBreakdown(period);
  const c = data?.currency || "KES";
  const rows = data?.partners || [];
  const totals = data?.totals;
  const ownPartnerTotal = sumAmounts(rows.map((p) => ownSplitOf(p).partner));
  const mainGrossTotal = sumAmounts(rows.map((p) => p.main?.gross));
  const mainPartnerTotal = sumAmounts(rows.map((p) => p.main?.split?.partner));
  const groupHead = "border-b border-gray-100 pb-1 pr-3 text-center text-[11px] font-semibold uppercase tracking-wide dark:border-white/5";
  return (
    <Card extra="p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Revenue by partner</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            What each partner earned in this period: their share of what their own markets earned and, where you turned it on, their share of the trading fees on your
            main markets from positions opened on their page or subdomain. Both come out of your {bpsLabel(data?.tenantShareBps ?? 8000)}.
          </p>
        </div>
        <Link to="/portal/partners" className="text-sm font-semibold text-brand-500">
          Manage partners →
        </Link>
      </div>
      {error && <div className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="text-gray-400">
              <th colSpan={3} />
              <th colSpan={2} className={groupHead}>
                Their own markets
              </th>
              <th colSpan={2} className={groupHead}>
                Main markets via their page
              </th>
              <th colSpan={2} className={groupHead}>
                Total
              </th>
            </tr>
            <tr className="text-xs text-gray-500">
              <th className="pb-2 pr-3 pt-1 font-semibold">Partner</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Markets</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Share (own / main)</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Gross</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Partner gets</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Gross</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Partner gets</th>
              <th className="pb-2 pr-3 pt-1 text-right font-semibold">Partner gets</th>
              <th className="pb-2 pt-1 text-right font-semibold">You keep</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && (
              <tr>
                <td colSpan={9} className="py-2">
                  <div className="h-6 animate-pulse rounded-md bg-accent" />
                </td>
              </tr>
            )}
            {data && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-sm text-gray-500">
                  No partners yet. Add one on the Partners page.
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr key={p.partnerId} className="border-t border-gray-100 dark:border-white/5">
                <td className="py-2 pr-3">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white">{p.name}</p>
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[p.status] || "bg-gray-100 text-gray-700"}`}>
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                </td>
                <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{p.marketCount}</td>
                <td className={`${numCell} text-navy-700 dark:text-gray-200`}>
                  {bpsLabel(p.shareBps)} / {p.main?.enabled ? bpsLabel(p.main.shareBps) : <span className="text-gray-400">off</span>}
                </td>
                <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{formatKes(p.gross, c)}</td>
                <td className={`${numCell} text-purple-600 dark:text-purple-300`}>{formatKes(ownSplitOf(p).partner, c)}</td>
                <td className={`${numCell} text-navy-700 dark:text-gray-200`}>{p.main ? formatKes(p.main.gross, c) : "—"}</td>
                <td className={`${numCell} text-purple-600 dark:text-purple-300`}>{p.main ? formatKes(p.main.split?.partner, c) : "—"}</td>
                <td className={`${numCell} font-semibold text-purple-600 dark:text-purple-300`}>{formatKes(p.split.partner, c)}</td>
                <td className="num whitespace-nowrap py-2 text-right text-sm font-semibold text-green-600 dark:text-green-400">{formatKes(p.split.tenantRetained, c)}</td>
              </tr>
            ))}
            {totals && rows.length > 0 && (
              <tr className="border-t-2 border-gray-200 dark:border-white/10">
                <td className="py-2 pr-3 text-sm font-semibold text-navy-700 dark:text-white">All partners</td>
                <td className={numCell} />
                <td className={numCell} />
                <td className={`${numCell} font-semibold text-navy-700 dark:text-white`}>{formatKes(totals.gross, c)}</td>
                <td className={`${numCell} font-semibold text-purple-600 dark:text-purple-300`}>{formatKes(ownPartnerTotal, c)}</td>
                <td className={`${numCell} font-semibold text-navy-700 dark:text-white`}>{formatKes(mainGrossTotal, c)}</td>
                <td className={`${numCell} font-semibold text-purple-600 dark:text-purple-300`}>{formatKes(mainPartnerTotal, c)}</td>
                <td className={`${numCell} font-semibold text-purple-600 dark:text-purple-300`}>{formatKes(totals.partner, c)}</td>
                <td className="num whitespace-nowrap py-2 text-right text-sm font-semibold text-green-600 dark:text-green-400">{formatKes(totals.tenantRetained, c)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function PartnerEarningsTiles({ revenue, portal }) {
  if (!revenue) return <TileSkeleton count={4} />;
  const p = revenue.partner;
  const c = revenue.currency;
  const tenantName = portal?.tenant?.name || "the tenant";
  const own = p.ownSplit ?? p.split;
  const mainHint = p.main && (p.main.enabled || Number(p.main.split?.partner) > 0)
    ? `, plus ${formatKes(p.main.split?.partner, c)} (${bpsLabel(p.main.shareBps)}) from main-market trades via your page`
    : "";
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile label="Commission from your markets" value={formatKes(p.gross, c)} hint="Trading commission and settlement surplus" />
      <Tile label="Net" value={formatKes(own.net, c)} tone="blue" hint="Net revenue your markets earned" />
      <Tile
        label="Your share"
        value={formatKes(p.split.partner, c)}
        tone="green"
        emphasis
        hint={`${bpsLabel(p.shareBps)} of ${tenantName}'s ${bpsLabel(revenue.tenantShareBps)} on your markets${mainHint}`}
      />
      <Tile label="Your markets" value={p.marketCount.toLocaleString()} hint="Markets that earned in this period" />
    </div>
  );
}

export function RevenueStatementView({ basePath, tenantId, period, onPeriodChange, toolbar }) {
  const { statement, error } = useRevenueStatement(basePath, { tenantId, period });
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker value={period} onChange={onPeriodChange} />
        {toolbar}
      </div>
      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
      <RevenueHeadline statement={statement} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-5">
        <Card extra="p-4 sm:p-5 xl:col-span-3">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Where every shilling goes</p>
          <p className="mb-4 mt-1 text-sm text-gray-600 dark:text-gray-300">
            Fees are charged on deposits and withdrawals, and commission on profits. The KES 1 SMS fee is yours untouched. What remains is net revenue: 80% to you, 20% to the infrastructure provider. Deposit and withdrawal fees are shown after payment processing. From your 80% of what a partner's markets earn, the partner gets the share you set. Every figure here is read from the ledger and reconciles with it.
          </p>
          <AllocationBar statement={statement} />
          <div className="mt-5">
            <VolumeStrip statement={statement} />
          </div>
        </Card>
        <Card extra="p-4 sm:p-5 xl:col-span-2">
          <p className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">Statement</p>
          {statement ? <RevenueWaterfall statement={statement} /> : <div className="h-72 animate-pulse rounded-md bg-accent" />}
        </Card>
      </div>
      <PartnerRevenueTable period={period} />
      <MoneyFlowLog basePath={basePath} tenantId={tenantId} period={period} />
    </div>
  );
}

export function RevenueSnapshot({ basePath, href, LinkComponent }) {
  const [period, setPeriod] = useState("30d");
  const { statement, error } = useRevenueStatement(basePath, { period });
  const More = LinkComponent;
  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Revenue</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Net revenue splits 80% to you and 20% to the infrastructure provider, with SMS fees to you. Deposit and withdrawal fees are shown after payment processing. Partners are paid from your 80%.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker value={period} onChange={setPeriod} />
            {More && href && (
              <More to={href} className="text-sm font-semibold text-brand-500">
                Full statement →
              </More>
            )}
          </div>
        </div>
        {error && <div className="mb-3 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
        <RevenueHeadline statement={statement} />
        <div className="mt-5">
          <AllocationBar statement={statement} />
        </div>
      </Card>
      <MoneyFlowLog basePath={basePath} period={period} pageSize={8} title="Latest money movements" />
    </div>
  );
}
